import { useState, useEffect, useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import {
  ShieldQuestion, Loader2, Check, X, CheckCircle2, XCircle,
  Users, Coins, CalendarClock, Send, Building2, ClipboardCheck,
  ListFilter, Trash2, PauseCircle, ExternalLink, MessageSquareText,
} from "lucide-react";
import {
  campaignStatusLabel, toCampaignStatus, CAMPAIGN_STATUSES, STATUS_BADGE,
  approvalStatusLabel, toApprovalStatus, APPROVAL_BADGE,
  holdReasonLabel, toHoldReason, segmentLabel, scheduleLabel,
} from "@wedding/sms-campaigns";
import type { SmsSegmentType, SmsScheduleType, RoutePath } from "@wedding/shared";
import {
  adminSmsApprovalsApi,
  type SmsApprovalRow, type SmsCampaignListRow, type SmsCampaignWeddingOption,
} from "../lib/api";
import { AppLink } from "../lib/route-primitives";
import { paths } from "../lib/paths";
import { cn } from "../lib/utils";

/**
 * Admin SMS Merkezi — İKİ SEKME:
 *   1. Onay kuyruğu (migration 047): yalnız `pending`, FIFO (en eski önce).
 *   2. Tüm kampanyalar: her düğün × her durum, LIFO (en yeni önce) + filtreler.
 *
 * ⚠️ NEDEN AYRI SAYFA, `SmsCampaignsPage`'e kart DEĞİL:
 * `SmsCampaignsPage` per-wedding'dir (`/:slug/sms`); bu sayfa ÇAPRAZ-WEDDING —
 * "hangi düğünde kim ne kadar harcıyor, hangi kampanya beklemede takılı"
 * sorusunun tek ekrandaki cevabı. Aynı gerekçeyle `moderation.tsx`/`support.tsx`/
 * `audit-log.tsx` da kendi global sayfaları.
 *
 * ⚠️ Kontrol Merkezi'yle ÇAKIŞMAZ: orada TOPLAM sayaçlar + harcama/devre kesici
 * var; burada kampanya KIRILIMI. Toplamdan tekile geçiş noktası.
 *
 * Yetki: bu sayfanın konuştuğu `/api/admin/sms-approvals` worker'da TEK mount'lu
 * ve `requireAdmin()` altında — organizer panelinde karşılığı yoktur.
 * (Worker yolu kasten `sms-approvals` kaldı: Pages ve Worker BAĞIMSIZ deploy
 * oluyor; yolu değiştirmek deploy aralığında 404 üretirdi — `control-center`
 * ile aynı karar.)
 */

const card = "bg-card dark:bg-[rgba(27,24,23,0.75)] dark:backdrop-blur-xl border border-border dark:border-primary/20 shadow-sm rounded-2xl overflow-hidden";
const selectCls = "rounded-xl border border-border bg-muted/30 px-3 py-2 text-sm text-foreground focus:border-primary/50 focus:bg-background transition-colors cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/50";

type Tab = "pending" | "all";
/** Bilinmeyen `?tab=` değeri sessizce kuyruğa düşer — 404 göstermeye değmez. */
function toTab(raw: string | null): Tab {
  return raw === "all" ? "all" : "pending";
}

function Toast({ msg, ok, onDone }: { msg: string; ok: boolean; onDone: () => void }) {
  useEffect(() => {
    const timer = setTimeout(onDone, 2800);
    return () => clearTimeout(timer);
  }, [onDone]);
  // Ekran okuyucu aksiyonun sonucunu duymalı — toast tek geri bildirim kanalı.
  return (
    <div role="status" aria-live="polite" className={cn(
      "fixed bottom-6 left-1/2 -translate-x-1/2 z-[200]",
      "flex items-center gap-2.5 px-4 py-3 rounded-2xl shadow-xl text-sm font-medium",
      ok ? "bg-emerald-500 text-white" : "bg-red-500 text-white",
    )}>
      {ok ? <CheckCircle2 className="h-4 w-4 shrink-0" /> : <XCircle className="h-4 w-4 shrink-0" />}
      {msg}
    </div>
  );
}

export function SmsCenterPage() {
  const { t } = useTranslation();
  // Sekme URL'de: yenileme/geri tuşu/paylaşılan bağlantı aynı sekmeye düşsün.
  // `useSearchParams` ham kalabilir — navigasyon HEDEFİ almaz (bkz. CLAUDE.md).
  const [params] = useSearchParams();
  const tab = toTab(params.get("tab"));
  const [toast, setToast] = useState<{ msg: string; ok: boolean } | null>(null);
  const showToast = useCallback((msg: string, ok = true) => setToast({ msg, ok }), []);
  /** Kuyruktaki bekleyen sayısı — sekme rozetinde. */
  const [pendingCount, setPendingCount] = useState<number | null>(null);

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-serif font-bold text-foreground flex items-center gap-2">
          <MessageSquareText className="h-6 w-6 text-primary" aria-hidden="true" />
          {t("sms.center.pageTitle")}
        </h1>
        <p className="text-sm text-muted-foreground mt-1">{t("sms.center.pageDescription")}</p>
      </div>

      {/* Sekmeler LİNK, `<button>` DEĞİL: durum zaten URL'de (`?tab=`), dolayısıyla
          gerçek bir navigasyon. Link olunca orta-tık / Cmd-tık yeni sekmede açar,
          sağ-tık "bağlantıyı kopyala" çalışır. `role="tablist"` KASITLI YOK —
          eksik ARIA (tabpanel/aria-controls olmadan) hiç ARIA'dan kötüdür;
          `aria-current` burada doğru ve tam olan semantiktir. */}
      <nav aria-label={t("sms.center.pageTitle")} className="flex gap-1 border-b border-border">
        <TabLink active={tab === "pending"} to={paths.smsCenterTab("pending")}
          icon={ClipboardCheck} label={t("sms.center.tabPending")} count={pendingCount} />
        <TabLink active={tab === "all"} to={paths.smsCenterTab("all")}
          icon={ListFilter} label={t("sms.center.tabAll")} count={null} />
      </nav>

      {/* Sekme değişince alt ağaç TAMAMEN değişir — mount/unmount kasıtlı:
          filtre state'i ve kuyruk state'i birbirine sızmasın. */}
      {tab === "pending"
        ? <PendingQueue showToast={showToast} onCount={setPendingCount} />
        : <AllCampaigns showToast={showToast} />}

      {toast && <Toast msg={toast.msg} ok={toast.ok} onDone={() => setToast(null)} />}
    </div>
  );
}

function TabLink({ active, to, icon: Icon, label, count }: {
  active: boolean; to: RoutePath;
  icon: React.ComponentType<{ className?: string }>; label: string; count: number | null;
}) {
  return (
    <AppLink to={to} aria-current={active ? "page" : undefined} replace
      className={cn(
        "inline-flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 -mb-px transition-colors cursor-pointer",
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/50 rounded-t-lg",
        active
          ? "border-primary text-foreground"
          : "border-transparent text-muted-foreground hover:text-foreground hover:border-border",
      )}>
      <Icon className="h-4 w-4 shrink-0" aria-hidden="true" />
      {label}
      {count !== null && count > 0 && (
        <span className="inline-flex items-center justify-center min-w-5 h-5 px-1.5 rounded-full text-[10px] font-bold bg-primary/15 text-primary">
          {count}
        </span>
      )}
    </AppLink>
  );
}

function PendingQueue({ showToast, onCount }: {
  showToast: (msg: string, ok?: boolean) => void;
  onCount: (n: number) => void;
}) {
  const { t, i18n } = useTranslation();
  const [items, setItems] = useState<SmsApprovalRow[]>([]);
  const [threshold, setThreshold] = useState<number | null>(null);
  const [cursor, setCursor] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [busy, setBusy] = useState<string | null>(null);
  /** Red formu açık olan kampanya + notu (not ZORUNLU değil). */
  const [rejecting, setRejecting] = useState<{ id: string; reason: string } | null>(null);

  const load = useCallback(async (nextCursor?: string) => {
    const append = Boolean(nextCursor);
    if (append) setLoadingMore(true); else setLoading(true);
    try {
      const res = await adminSmsApprovalsApi.list(nextCursor);
      const data = res.data;
      if (!data) return;
      setItems((prev) => {
        const next = append ? [...prev, ...data.items] : data.items;
        onCount(next.length);
        return next;
      });
      setCursor(data.nextCursor);
      setThreshold(data.threshold);
    } catch (e) {
      showToast(e instanceof Error ? e.message : t("common.error"), false);
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, [showToast, t, onCount]);

  useEffect(() => { void load(); }, [load]);

  /** Karara bağlanan kampanya kuyruktan DÜŞER (sunucu da artık pending döndürmez). */
  const drop = (id: string) => setItems((prev) => {
    const next = prev.filter((c) => c.id !== id);
    onCount(next.length);
    return next;
  });

  const approve = async (id: string) => {
    setBusy(id);
    try {
      await adminSmsApprovalsApi.approve(id);
      drop(id);
      showToast(t("sms.approval.approveSuccess"));
    } catch (e) {
      // 409 dahil: başka bir admin önce karar vermiş olabilir → listeyi tazele.
      showToast(e instanceof Error ? e.message : t("sms.approval.actionError"), false);
      void load();
    } finally {
      setBusy(null);
    }
  };

  const reject = async (id: string, reason: string) => {
    setBusy(id);
    try {
      await adminSmsApprovalsApi.reject(id, reason.trim() || undefined);
      drop(id);
      setRejecting(null);
      showToast(t("sms.approval.rejectSuccess"));
    } catch (e) {
      showToast(e instanceof Error ? e.message : t("sms.approval.actionError"), false);
      void load();
    } finally {
      setBusy(null);
    }
  };

  const fmtDate = (raw: string | null) =>
    raw ? new Date(raw.replace(" ", "T") + "Z").toLocaleString(i18n.language) : null;

  return (
    <div className="space-y-4">
      {threshold !== null && (
        <p className="text-xs text-muted-foreground">
          {t("sms.approval.thresholdNote", { count: threshold })}
        </p>
      )}

      {loading ? (
        <div className={cn(card, "p-10 flex justify-center")}>
          <Loader2 className="h-6 w-6 animate-spin text-primary" />
        </div>
      ) : items.length === 0 ? (
        <div className={cn(card, "flex flex-col items-center gap-2 py-16")}>
          <ShieldQuestion className="h-8 w-8 text-primary/20" aria-hidden="true" />
          <p className="text-sm font-medium text-foreground">{t("sms.approval.empty")}</p>
          <p className="text-xs text-muted-foreground">{t("sms.approval.emptyHint")}</p>
        </div>
      ) : (
        <div className="space-y-4">
          {items.map((c) => {
            const isBusy = busy === c.id;
            const isRejecting = rejecting?.id === c.id;
            return (
              <div key={c.id} className={cn(card, "p-5 space-y-4")}>
                <div className="flex items-start gap-3 flex-wrap">
                  <div className="flex-1 min-w-48">
                    <p className="text-sm font-semibold text-foreground">{c.name}</p>
                    <p className="text-[11px] text-muted-foreground mt-0.5 flex items-center gap-1.5 flex-wrap">
                      <Building2 className="h-3 w-3 shrink-0" aria-hidden="true" />
                      {c.wedding_title}
                      <span className="opacity-50">/{c.wedding_slug}</span>
                      {c.organizer_email && <>· {c.organizer_name ?? c.organizer_email}</>}
                    </p>
                  </div>
                  <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-[10px] font-bold uppercase ring-1 ring-inset bg-indigo-500/10 text-indigo-600 dark:text-indigo-400 ring-indigo-500/20">
                    <ShieldQuestion className="h-3 w-3 shrink-0" aria-hidden="true" />
                    {t("sms.approval.pendingBadge")}
                  </span>
                </div>

                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                  <Stat icon={Users} label={t("sms.approval.recipients", { count: c.approval_recipient_count ?? 0 })}
                    value={c.approval_recipient_count === null
                      ? t("sms.approval.recipientsUnknown")
                      : String(c.approval_recipient_count)} />
                  <Stat icon={Coins} label={t("sms.approval.estimatedCost")}
                    value={c.estimatedCostUsd === null ? "—" : `$${c.estimatedCostUsd.toFixed(2)}`}
                    hint={t("sms.approval.segmentsPerMessage", { count: c.segmentsPerMessage })} />
                  <Stat icon={CalendarClock} label={t("sms.approval.scheduledFor")}
                    value={fmtDate(c.next_dispatch_at) ?? t("sms.approval.sendNowLabel")} />
                  <Stat icon={Send} label={t("sms.approval.createdAt")}
                    value={fmtDate(c.created_at) ?? "—"} />
                </div>

                <div>
                  <p className="text-[11px] font-medium text-muted-foreground mb-1">
                    {t("sms.approval.messagePreview")}
                  </p>
                  <pre className="text-xs text-foreground whitespace-pre-wrap break-words bg-muted/30 rounded-xl p-3 font-sans">
                    {c.messagePreview}
                  </pre>
                </div>

                {isRejecting ? (
                  <div className="space-y-2">
                    <label htmlFor={`reason-${c.id}`} className="block text-[11px] font-medium text-muted-foreground">
                      {t("sms.approval.rejectReasonLabel")}
                    </label>
                    <input
                      id={`reason-${c.id}`}
                      value={rejecting.reason}
                      onChange={(e) => setRejecting({ id: c.id, reason: e.target.value })}
                      placeholder={t("sms.approval.rejectReasonPlaceholder")}
                      maxLength={500}
                      className="w-full rounded-xl border border-border bg-muted/30 px-3 py-2.5 text-sm focus:outline-none focus:border-primary/50 focus:bg-background transition-colors"
                    />
                    <div className="flex gap-2 justify-end">
                      <button type="button" onClick={() => setRejecting(null)} disabled={isBusy}
                        className="px-3 py-2 rounded-xl text-sm font-medium text-muted-foreground hover:bg-muted/60 transition-colors cursor-pointer disabled:opacity-40">
                        {t("sms.approval.rejectCancel")}
                      </button>
                      <button type="button" onClick={() => void reject(c.id, rejecting.reason)} disabled={isBusy}
                        className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl text-sm font-semibold bg-rose-500 text-white hover:bg-rose-600 transition-colors cursor-pointer disabled:opacity-40">
                        {isBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <X className="h-4 w-4" />}
                        {isBusy ? t("sms.approval.rejecting") : t("sms.approval.rejectConfirm")}
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="flex gap-2 justify-end">
                    <button type="button" onClick={() => setRejecting({ id: c.id, reason: "" })} disabled={isBusy}
                      className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl text-sm font-medium text-rose-600 dark:text-rose-400 hover:bg-rose-500/10 transition-colors cursor-pointer disabled:opacity-40">
                      <X className="h-4 w-4" />
                      {t("sms.approval.reject")}
                    </button>
                    <button type="button" onClick={() => void approve(c.id)} disabled={isBusy}
                      className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl text-sm font-semibold bg-emerald-500 text-white hover:bg-emerald-600 transition-colors cursor-pointer disabled:opacity-40">
                      {isBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
                      {isBusy ? t("sms.approval.approving") : t("sms.approval.approve")}
                    </button>
                  </div>
                )}
              </div>
            );
          })}

          {cursor && (
            <button type="button" onClick={() => void load(cursor)} disabled={loadingMore}
              className="w-full py-3 rounded-2xl border border-border text-sm font-medium text-muted-foreground hover:bg-muted/40 transition-colors cursor-pointer disabled:opacity-40">
              {loadingMore ? <Loader2 className="h-4 w-4 animate-spin mx-auto" /> : t("sms.approval.loadMore")}
            </button>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Sekme 2: çapraz-düğün tüm kampanyalar ───────────────────────────────────
function AllCampaigns({ showToast }: { showToast: (msg: string, ok?: boolean) => void }) {
  const { t, i18n } = useTranslation();
  const [items, setItems] = useState<SmsCampaignListRow[]>([]);
  const [weddings, setWeddings] = useState<SmsCampaignWeddingOption[]>([]);
  const [cursor, setCursor] = useState<string | null>(null);
  const [priceUsd, setPriceUsd] = useState(0);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [status, setStatus] = useState("");
  const [approval, setApproval] = useState("");
  const [weddingId, setWeddingId] = useState("");
  const [includeDeleted, setIncludeDeleted] = useState(false);

  // Primitif bağımlılıklar (`rerender-dependencies`): filtre objesi her render'da
  // yeni referans olurdu → `load` sonsuz yeniden kurulurdu.
  const load = useCallback(async (nextCursor?: string) => {
    const append = Boolean(nextCursor);
    if (append) setLoadingMore(true); else setLoading(true);
    try {
      const res = await adminSmsApprovalsApi.listAll(
        { status: status || undefined, approval: approval || undefined,
          weddingId: weddingId || undefined, includeDeleted },
        nextCursor,
      );
      const data = res.data;
      if (!data) return;
      setItems((prev) => (append ? [...prev, ...data.items] : data.items));
      setCursor(data.nextCursor);
      setPriceUsd(data.priceUsd);
    } catch (e) {
      showToast(e instanceof Error ? e.message : t("common.error"), false);
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, [status, approval, weddingId, includeDeleted, showToast, t]);

  // Filtre değişince BAŞTAN yükle — imleç eski filtreye aitti, taşımak
  // sayfa sınırında yanlış sonuç verirdi.
  useEffect(() => { void load(); }, [load]);

  // Düğün seçeneği listesi filtreden BAĞIMSIZ (bir kez) — kendi seçimimiz
  // seçeneği listeden düşürmemeli.
  useEffect(() => {
    void (async () => {
      try {
        const res = await adminSmsApprovalsApi.weddings();
        setWeddings(res.data?.items ?? []);
      } catch { /* seçici boş kalır; liste yine çalışır */ }
    })();
  }, []);

  const pageSpend = useMemo(
    () => items.reduce((sum, c) => sum + c.spentUsd, 0),
    [items],
  );
  const pageSegments = useMemo(
    () => items.reduce((sum, c) => sum + c.segments_used, 0),
    [items],
  );

  const fmtDate = (raw: string | null) =>
    raw ? new Date(raw.replace(" ", "T") + "Z").toLocaleString(i18n.language) : null;

  const resetFilters = () => {
    setStatus(""); setApproval(""); setWeddingId(""); setIncludeDeleted(false);
  };
  const hasFilters = Boolean(status || approval || weddingId || includeDeleted);

  return (
    <div className="space-y-4">
      {/* Filtreler */}
      <div className={cn(card, "p-4 flex flex-wrap items-center gap-3")}>
        <label className="sr-only" htmlFor="f-wedding">{t("sms.center.filterWedding")}</label>
        <select id="f-wedding" value={weddingId} onChange={(e) => setWeddingId(e.target.value)} className={selectCls}>
          <option value="">{t("sms.center.filterWeddingAll")}</option>
          {weddings.map((w) => (
            <option key={w.id} value={w.id}>{w.title} ({w.campaign_count})</option>
          ))}
        </select>

        <label className="sr-only" htmlFor="f-status">{t("sms.center.filterStatus")}</label>
        <select id="f-status" value={status} onChange={(e) => setStatus(e.target.value)} className={selectCls}>
          <option value="">{t("sms.center.filterStatusAll")}</option>
          {CAMPAIGN_STATUSES.map((s) => (
            <option key={s} value={s}>{campaignStatusLabel(t, s)}</option>
          ))}
        </select>

        <label className="sr-only" htmlFor="f-approval">{t("sms.center.filterApproval")}</label>
        <select id="f-approval" value={approval} onChange={(e) => setApproval(e.target.value)} className={selectCls}>
          <option value="">{t("sms.center.filterApprovalAll")}</option>
          <option value="pending">{t("sms.approval.pendingBadge")}</option>
          <option value="approved">{t("sms.approval.approvedBadge")}</option>
          <option value="rejected">{t("sms.approval.rejectedBadge")}</option>
          <option value="none">{t("sms.center.filterApprovalNone")}</option>
        </select>

        <label className="inline-flex items-center gap-2 text-sm text-muted-foreground cursor-pointer">
          <input type="checkbox" checked={includeDeleted} onChange={(e) => setIncludeDeleted(e.target.checked)}
            className="rounded border-border cursor-pointer" />
          {t("sms.center.filterIncludeDeleted")}
        </label>

        {hasFilters && (
          <button type="button" onClick={resetFilters}
            className="ml-auto text-xs font-medium text-primary hover:underline cursor-pointer">
            {t("sms.center.filterReset")}
          </button>
        )}
      </div>

      {/* Sayfa toplamı — GERÇEKLEŞEN harcama, tahmin değil.
          Platform geneli toplam Kontrol Merkezi'nde; burada görünen sayfaya aittir. */}
      {!loading && items.length > 0 && (
        <p className="text-xs text-muted-foreground px-1">
          {t("sms.center.pageTotals", {
            count: items.length,
            segments: pageSegments,
            usd: pageSpend.toFixed(2),
          })}
        </p>
      )}

      {loading ? (
        <div className={cn(card, "p-10 flex justify-center")}>
          <Loader2 className="h-6 w-6 animate-spin text-primary" />
        </div>
      ) : items.length === 0 ? (
        <div className={cn(card, "flex flex-col items-center gap-2 py-16")}>
          <ListFilter className="h-8 w-8 text-primary/20" aria-hidden="true" />
          <p className="text-sm font-medium text-foreground">{t("sms.center.empty")}</p>
          <p className="text-xs text-muted-foreground">
            {hasFilters ? t("sms.center.emptyFiltered") : t("sms.center.emptyHint")}
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {items.map((c) => (
            <CampaignCard key={c.id} c={c} priceUsd={priceUsd} fmtDate={fmtDate} />
          ))}

          {cursor && (
            <button type="button" onClick={() => void load(cursor)} disabled={loadingMore}
              className="w-full py-3 rounded-2xl border border-border text-sm font-medium text-muted-foreground hover:bg-muted/40 transition-colors cursor-pointer disabled:opacity-40">
              {loadingMore ? <Loader2 className="h-4 w-4 animate-spin mx-auto" /> : t("sms.approval.loadMore")}
            </button>
          )}
        </div>
      )}
    </div>
  );
}

function CampaignCard({ c, priceUsd, fmtDate }: {
  c: SmsCampaignListRow; priceUsd: number;
  fmtDate: (raw: string | null) => string | null;
}) {
  const { t } = useTranslation();
  // API ham TEXT döndürür → union'a daralt; bilinmeyen değerde rozeti hiç çizme
  // (çevirisi olmayan anahtar aramaktan iyidir). Gerekçe: labels.ts.
  const st = toCampaignStatus(c.status);
  const appr = toApprovalStatus(c.approval_status);
  const hold = toHoldReason(c.hold_reason);
  const isDeleted = c.deleted_at !== null;

  return (
    <div className={cn(card, "p-4 space-y-3", isDeleted && "opacity-60")}>
      <div className="flex items-start gap-3 flex-wrap">
        <div className="flex-1 min-w-48">
          <p className={cn("text-sm font-semibold text-foreground break-words", isDeleted && "line-through")}>
            {c.name}
          </p>
          <p className="text-[11px] text-muted-foreground mt-0.5 flex items-center gap-1.5 flex-wrap">
            <Building2 className="h-3 w-3 shrink-0" aria-hidden="true" />
            {/* Düğünün KENDİ SMS sayfasına geçiş — çapraz listeden tekile inme yolu. */}
            <AppLink to={paths.wedding.sms(c.wedding_slug)}
              className="hover:text-primary hover:underline inline-flex items-center gap-1 cursor-pointer">
              {c.wedding_title}
              <ExternalLink className="h-2.5 w-2.5 shrink-0" aria-hidden="true" />
            </AppLink>
            <span className="opacity-50">/{c.wedding_slug}</span>
            {c.organizer_email && <>· {c.organizer_name ?? c.organizer_email}</>}
          </p>
        </div>
        <div className="flex items-center gap-1.5 flex-wrap">
          {st && (
            <span className={cn("inline-flex items-center px-2.5 py-0.5 rounded-full text-[10px] font-bold uppercase ring-1 ring-inset", STATUS_BADGE[st])}>
              {campaignStatusLabel(t, st)}
            </span>
          )}
          {appr && (
            <span className={cn("inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-[10px] font-bold uppercase ring-1 ring-inset", APPROVAL_BADGE[appr])}>
              <ShieldQuestion className="h-3 w-3 shrink-0" aria-hidden="true" />
              {approvalStatusLabel(t, appr)}
            </span>
          )}
          {/* `hold_reason` ile `approval_status` ORTOGONAL — ikisi birden çizilebilir. */}
          {hold && (
            <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-[10px] font-bold uppercase ring-1 ring-inset bg-amber-500/10 text-amber-600 dark:text-amber-400 ring-amber-500/20"
              title={holdReasonLabel(t, hold)}>
              <PauseCircle className="h-3 w-3 shrink-0" aria-hidden="true" />
              {t("sms.holdBadge")}
            </span>
          )}
          {isDeleted && (
            <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-[10px] font-bold uppercase ring-1 ring-inset bg-muted/60 text-muted-foreground ring-border">
              <Trash2 className="h-3 w-3 shrink-0" aria-hidden="true" />
              {t("sms.center.deletedBadge")}
            </span>
          )}
        </div>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5">
        <Stat icon={Coins} label={t("sms.center.spent")}
          value={`$${c.spentUsd.toFixed(2)}`}
          hint={t("sms.center.segmentsUsed", { count: c.segments_used })} />
        <Stat icon={Send} label={t("sms.center.sends")}
          value={String(c.sends_total)}
          hint={t("sms.center.deliveredFailed", { delivered: c.delivered_count, failed: c.failed_count })} />
        <Stat icon={Users} label={t("sms.center.audience")}
          value={segmentLabel(t, c.segment_type as SmsSegmentType)}
          hint={scheduleLabel(t, c.schedule_type as SmsScheduleType)} />
        <Stat icon={CalendarClock} label={t("sms.center.lastDispatch")}
          value={fmtDate(c.last_dispatched_at) ?? "—"}
          hint={c.next_dispatch_at ? t("sms.center.nextDispatch", { at: fmtDate(c.next_dispatch_at) }) : undefined} />
      </div>

      {c.rejection_reason && (
        <p className="text-[11px] text-rose-600 dark:text-rose-400 bg-rose-500/5 rounded-xl px-3 py-2">
          {t("sms.center.rejectionReason", { reason: c.rejection_reason })}
        </p>
      )}

      <p className="text-[10px] text-muted-foreground">
        {t("sms.center.createdAt", { at: fmtDate(c.created_at) ?? "—" })}
        {priceUsd > 0 && ` · ${t("sms.center.unitPrice", { usd: priceUsd })}`}
      </p>
    </div>
  );
}

function Stat({ icon: Icon, label, value, hint }: {
  icon: React.ComponentType<{ className?: string }>;
  label: string; value: string; hint?: string;
}) {
  return (
    <div className="rounded-xl bg-muted/30 px-3 py-2.5">
      <p className="text-[10px] uppercase font-semibold text-muted-foreground flex items-center gap-1">
        <Icon className="h-3 w-3 shrink-0" aria-hidden="true" />
        {label}
      </p>
      <p className="text-sm font-semibold text-foreground mt-0.5 break-words">{value}</p>
      {hint && <p className="text-[10px] text-muted-foreground mt-0.5">{hint}</p>}
    </div>
  );
}
